// src/lib/morningRoutine.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The pure half of the morning routine on Today. The
//               component lives in pages/Dashboard/components/MorningRoutine.jsx
//               and touches Supabase and localStorage; everything here is
//               arithmetic and strings, so node can test it.
//
// THE FRONT DOOR, BUILT AROUND THE DAY INSTEAD OF THE COURSE.
//
// Today used to open on the curriculum: your lesson, your quiz, your
// reflection. A salesperson walks in thinking about a ten o'clock and the
// three people who walked yesterday, and nobody on a floor has ever said they
// could not wait to do their reflection.
//
// So the first thing on the screen is now a two minute routine in three moves,
// in the order a good manager would run it at the desk before the doors open:
//
//   1. YESTERDAY.   Did you do the thing you committed to? One tap.
//   2. YOUR UNSOLD. How many walked without buying? Practise one callback.
//   3. ONE REP.     Sixty seconds on today's step. The batting cage.
//
// Then close the app and go sell. It should feel like stretching, not homework.
//
// WHY THOSE THREE AND IN THAT ORDER.
//
// The first is Jazz's core loop, commitment then inspection, which was already
// on this screen as a card. The second is the salesperson half of one of his six
// daily disciplines for a manager: review every unsold customer before they go
// cold. The manager had that discipline and the salesperson had nothing, which
// is backwards, because the salesperson is the one who has to make the call.
// The third is his own teaching method: short repetitions, one behaviour at a
// time, before it matters.
//
// NOTHING HERE STORES A CUSTOMER. The unsold step records a COUNT. The callback
// practice happens in the coach, where the charter already forbids deal data
// and the follow up block says it again at the moment the temptation is
// highest. A count of people who walked is a fact about the salesperson's day,
// not about anybody who walked.
//
// Voice rules: no Oxford commas. No em dashes. Real apostrophes.

import { dayKey } from './localDate.js'

// 0 to 4 and then "5 or more". Nobody needs to count past five before a shift,
// and a free number field is a keyboard on a phone at 8:50 in the morning.
export const UNSOLD_CHOICES = [0, 1, 2, 3, 4, 5]
export const unsoldLabel = (n) => (n >= 5 ? '5+' : String(n))

export const STEPS = ['yesterday', 'unsold', 'rep']

// One key per person per day. A routine is a daily thing and yesterday's
// answers must not carry into today.
export function routineKey(userId, day = dayKey(new Date())) {
  return `j13.morning.${userId || 'anon'}.${day}`
}

// A blank day. `yesterday` starts as null rather than false because "not
// answered" and "answered missed" are different, and the render treats them
// differently.
export function emptyRoutine() {
  return { yesterday: null, unsold: null, rep: false, finishedAt: null }
}

// Read a saved routine defensively. localStorage can hand back anything,
// including a shape from an older version of this file, and a malformed value
// must produce a fresh morning rather than a crash on the front door.
export function parseRoutine(raw) {
  if (!raw) return emptyRoutine()
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw
    const out = emptyRoutine()
    if (v.yesterday === 'kept' || v.yesterday === 'missed' || v.yesterday === 'none') out.yesterday = v.yesterday
    if (Number.isInteger(v.unsold) && v.unsold >= 0 && v.unsold <= 5) out.unsold = v.unsold
    if (v.rep === true) out.rep = true
    if (typeof v.finishedAt === 'string') out.finishedAt = v.finishedAt
    return out
  } catch {
    return emptyRoutine()
  }
}

// Which steps are finished. "No commitment to check" counts as finished, so a
// brand new rep with nothing to inspect is not left staring at a step they
// cannot complete.
export function doneSteps(state, { hasCommitment }) {
  return {
    yesterday: !hasCommitment || state.yesterday !== null,
    unsold: state.unsold !== null,
    rep: state.rep === true,
  }
}

// The step to point at: the first one not finished, or null when all are.
export function currentStep(state, opts) {
  const d = doneSteps(state, opts)
  return STEPS.find((s) => !d[s]) || null
}

export function isComplete(state, opts) {
  return currentStep(state, opts) === null
}

// What the rep "says" to open the follow up session. In their voice, because
// it goes into the transcript as their message, and phrased so the coach knows
// exactly what is being asked without a mode it has to guess at.
export function followupOpener(n) {
  if (!Number.isInteger(n) || n < 1) return 'Help me follow up with a customer who walked yesterday.'
  const who = n >= 5 ? 'Five or more people' : n === 1 ? 'One person' : `${['', '', 'Two', 'Three', 'Four'][n]} people`
  return `${who} walked yesterday without buying. Help me follow up with one of them.`
}

export function followupLink(n) {
  const safe = Number.isInteger(n) && n >= 1 && n <= 5 ? n : 1
  return `/coach/?mode=followup&unsold=${safe}`
}

// The rep is a drill on the step they are working. The coach's practice mode
// already picks a real scenario for the step and runs it as a scored three
// exchange drill, so this is a link rather than a new feature.
export function repLink(stepNumber) {
  const n = Number(stepNumber)
  return Number.isInteger(n) && n >= 1 && n <= 13 ? `/coach/?practice=${n}` : '/coach/'
}

// The line under the routine once it is done. Short, and it tells the truth
// about what they did rather than cheering.
export function finishedLine(state) {
  const parts = []
  if (state.yesterday === 'kept') parts.push('commitment kept')
  if (Number.isInteger(state.unsold) && state.unsold > 0) parts.push(`${unsoldLabel(state.unsold)} to follow up`)
  if (state.rep) parts.push('one rep in')
  return parts.length ? parts.join(', ') : 'warmed up'
}
